import { Paper, Flex, Text, ActionIcon } from '@mantine/core';
import { IconArrowUpRight, IconEye, IconPencil } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';

import {
  VacancyAcivityDrawer,
  useVacancyChangeActivityModal,
  useVacationEmployerChange,
} from '@features/vacancy';
import { useParams, Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  useUpdateVacancyStatus,
  type UpdateVacancyStatusRequest,
  type Vacancy,
  type VacancyStatusesHistory,
} from '@entities/vacancy';
import { QueryKey } from '@shared/config';
import { showNotification } from '@shared/utils';
import { VACANCY_STATUS_DISABLED_STATUS } from './constants';
import { VacancyStatus } from '@widgets/vacancy/vacancyStatus';
import dayjs from 'dayjs';
import { VACANCY_STATUS_MODEL } from '@widgets/vacancy/vacancyStatus/constants';

type VacancyActivityBlockProps = {
  responsibleUserId: string;
  vacancyId: string;
  isMe: boolean;
  activeFrom: string | null;
  activeTo: string | null;
  status: string;
  responsibleUserName: string;
  responsibleUserLastName: string;
  responsibleUserRole: string;
  statusesHistory: VacancyStatusesHistory[];
};

export const VacancyActivityBlock = ({
  responsibleUserId,
  vacancyId,
  isMe,
  activeFrom,
  activeTo,
  status,
  responsibleUserName,
  responsibleUserLastName,
  responsibleUserRole,
  statusesHistory,
}: VacancyActivityBlockProps) => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [opened, { open, close }] = useDisclosure(false);
  const { openChangeActivityVacancy } = useVacancyChangeActivityModal();
  const { openChangeVacationEmployer } = useVacationEmployerChange();

  const {
    mutate: updateVacancyStatus,
    isPending: isUpdateVacancyStatusPending,
  } = useUpdateVacancyStatus(id || '');

  const handleOpenChangeActivityVacancy = () => {
    openChangeActivityVacancy(vacancyId, status);
  };

  const handleOpenChangeVacationEmployer = () => {
    openChangeVacationEmployer(responsibleUserId, vacancyId);
  };

  const handleStatusChange = (status: string) => {
    const body: UpdateVacancyStatusRequest = {
      status,
    };

    if (status === VACANCY_STATUS_MODEL.IN_PROGRESS) {
      body.activeFrom = new Date().toISOString();
    }

    updateVacancyStatus(body, {
      onSuccess: data => {
        queryClient.setQueryData(
          [QueryKey.GET_VACANCY, Number(id)],
          (prev: Vacancy) => ({ ...prev, ...data })
        );

        queryClient.invalidateQueries({
          queryKey: [QueryKey.GET_VACANCIES],
        });

        queryClient.invalidateQueries({
          queryKey: [QueryKey.GET_VACANCY_STATUS_HISTORY],
        });
      },
      onError: () => {
        showNotification({
          variant: 'error',
          title: 'Не удалось изменить статус вакансии',
        });
      },
    });
  };

  const isShowVacancyActivity = status === VACANCY_STATUS_MODEL.IN_PROGRESS;
  return (
    <>
      <Paper
        p={24}
        radius={16}
        bg='primary.0'
        w='100%'
        maw={418}
        miw={{ base: 'auto', sm: '26rem' }}
      >
        <Flex direction='column' gap={24}>
          <Text size='lg' c='primary.7' fw={600}>
            Статус вакансии
          </Text>

          <Flex
            w='100%'
            pb={24}
            style={{
              borderBottom: '1px solid var(--mantine-color-tertiary-1)',
            }}
          >
            <VacancyStatus
              status={status}
              onStatusChange={handleStatusChange}
              disabled={VACANCY_STATUS_DISABLED_STATUS.includes(status)}
              loading={isUpdateVacancyStatusPending}
            />
          </Flex>

          <Flex gap={16} align='center' justify='space-between'>
            <Text size='lg' c='primary.7' fw={600}>
              Начало работы (или период работы для временных вакансий)
            </Text>

            <ActionIcon size={32} variant='transparent' onClick={() => open()}>
              <IconEye color='var(--mantine-color-primary-7)' size={24} />
            </ActionIcon>
          </Flex>

          <Flex
            justify='space-between'
            wrap='wrap'
            gap={4}
            w='100%'
            align='center'
            pb={isShowVacancyActivity ? 24 : 0}
            style={{
              borderBottom: '1px solid var(--mantine-color-tertiary-1)',
            }}
          >
            {isShowVacancyActivity && (
              <>
                <Flex gap={4} align='center'>
                  <Text c='tertiary.7'>с</Text>
                  <Text c='primary.7'>
                    {activeFrom ? dayjs(activeFrom).format('DD.MM.YYYY') : '-'}
                  </Text>
                  {activeTo && (
                    <>
                      <Text c='tertiary.7'>до</Text>
                      <Text c='primary.7'>
                        {activeTo ? dayjs(activeTo).format('DD.MM.YYYY') : '-'}
                      </Text>
                    </>
                  )}
                </Flex>

                <ActionIcon
                  size={32}
                  variant='transparent'
                  onClick={handleOpenChangeActivityVacancy}
                >
                  <IconPencil
                    color='var(--mantine-color-secondary-7)'
                    size={24}
                  />
                </ActionIcon>
              </>
            )}
          </Flex>

          <Flex gap={16} align='center' justify='space-between'>
            <Text c='tertiary.7' fw={600} size='lg'>
              Ответственный сотрудник
            </Text>

            <ActionIcon
              size={32}
              variant='transparent'
              onClick={handleOpenChangeVacationEmployer}
            >
              <IconPencil color='var(--mantine-color-secondary-7)' size={24} />
            </ActionIcon>
          </Flex>

          <Flex justify='space-between'>
            <Flex gap={8} align='center'>
              <Flex gap={4} direction='column'>
                <Text c='primary.7'>
                  {`${responsibleUserName}, ${responsibleUserLastName}`}
                </Text>
                <Text size='sm' c='tertiary.7'>
                  {!isMe ? responsibleUserRole : `${responsibleUserRole} (я)`}
                </Text>
              </Flex>
            </Flex>

            <ActionIcon component={Link} to={`/user-list/${responsibleUserId}`}>
              <IconArrowUpRight
                size={24}
                color='var(--mantine-color-primary-7)'
              />
            </ActionIcon>
          </Flex>
        </Flex>
      </Paper>

      <VacancyAcivityDrawer
        opened={opened}
        close={close}
        statusesHistory={statusesHistory || []}
      />
    </>
  );
};
